import { EmployeeInvitationStatusTypes, Subscriber } from 'types/electionService';
import { action, computed, makeObservable, observable } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { EmployeeActionObject, employeeActionsFiltersPipeline } from './actionRules';
import { SendInvitationModalStore } from './ui-stores/sendInvitationModalStore';
import { DefaultDomainParams } from './iAddEmployeesPage';

export interface EmployeeActionUiPresenter {
  type: string;
  name: string;
  handler: () => Promise<void> | void;
}

export interface ActionsUiStores {
  sendInvitationModalStore: SendInvitationModalStore | null;
  warningModalDelegates: Record<string, ModalDelegate>;
}

export interface ConstructorParams {
  employee: Subscriber;
  availableActions: EmployeeActionObject[];
  refreshData: () => Promise<void>;
  setPageLoading: (loading: boolean) => void;
}

export default class EmployeeDelegate {
  private _data: Subscriber;
  private _domainParams: DefaultDomainParams;
  private _actionList: EmployeeActionObject[];
  private _refreshData: () => Promise<void>;
  private _setPageLoading: (loading: boolean) => void;
  private _sendInvitationModalStore: SendInvitationModalStore | null = null;
  private _warningModalDelegates: Record<string, ModalDelegate> = {};

  get rawData(): Subscriber {
    return this._data;
  }

  get fullName(): string {
    return `${this._data.common.firstName} ${this._data.common.middleName || ''} ${this._data.common.lastName}`;
  }

  get dependentsNumber(): number {
    return this._data.custom.dependents.length;
  }

  get availableActions(): EmployeeActionUiPresenter[] {
    // Capture observable values inside the reactive context to avoid accessing them later outside reactive context
    const groupId = this._domainParams.groupId;
    const periodId = this._domainParams.periodId;
    const employeeData = this._data;
    const setPageIsLoading = this._setPageLoading;
    const refreshData = this._refreshData;
    const openSendInvitationModal = this._openSendInvitationModal.bind(this);

    return this._actionList.map((actionObj) => {
      const handler = action(async (): Promise<void> => {
        await actionObj.handler({
          interactions: {
            setPageIsLoading,
            refreshData,
          },
          ui: {
            openSendInvitationModal,
          },
          static: {
            employeeData,
            groupId,
            periodId,
            cartId: this._domainParams.cartId,
          },
        });
      });
      return {
        type: actionObj.type,
        name: actionObj.name,
        handler,
      };
    });
  }

  public get actionsUiStores(): ActionsUiStores {
    return {
      sendInvitationModalStore: this._sendInvitationModalStore,
      warningModalDelegates: this._warningModalDelegates,
    };
  }

  private _openSendInvitationModal(): void {
    if (this._sendInvitationModalStore) {
      this._sendInvitationModalStore.open();
    }
  }

  constructor(
    employee: Subscriber,
    availableActions: EmployeeActionObject[],
    domainParams: DefaultDomainParams,
    refreshData: () => Promise<void>,
    setPageLoading: (loading: boolean) => void,
  ) {
    this._data = employee;
    this._domainParams = domainParams;
    this._refreshData = refreshData;
    this._setPageLoading = setPageLoading;

    this._actionList = employeeActionsFiltersPipeline({
      actionList: availableActions,
      employeeStatus: this._data.selfEnrollStatus || EmployeeInvitationStatusTypes.NOT_YET_INVITED,
      employeeProgress: this._data.progress,
    });

    availableActions.forEach((availableAction) => {
      if (availableAction.isMissclikable) {
        this._warningModalDelegates[availableAction.type] = new ModalDelegate();
      }
    });

    this._sendInvitationModalStore = new SendInvitationModalStore({
      employee,
      groupId: domainParams.groupId,
      refreshData,
    });

    makeObservable<
      EmployeeDelegate,
      '_sendInvitationModalStore' | '_openSendInvitationModal' | '_warningModalDelegates'
    >(this, {
      _sendInvitationModalStore: observable,
      _warningModalDelegates: observable,
      rawData: computed,
      fullName: computed,
      dependentsNumber: computed,
      availableActions: computed,
      actionsUiStores: computed,
      _openSendInvitationModal: action.bound,
    });
  }
}
